// utils/emailService.ts
/**
 * Interface for booking details with period support
 */
export interface BookingDetails {
  serviceType: string;
  fromDate: string;      // Start date
  toDate: string;        // End date
  time: string;
  duration?: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  address?: string;
  notes?: string;
  bookingId?: string;
  periodType?: 'single-day' | 'multi-day' | 'recurring';
}

/**
 * Opens the default email client with a booking confirmation email
 * @param bookingDetails - The booking details object
 * @returns boolean - Returns true if successful
 */
export const sendBookingConfirmationEmail = (
  bookingDetails: BookingDetails
): boolean => {
  try {
    const {
      serviceType,
      fromDate,
      toDate,
      time,
      duration = 'Standard duration',
      customerName,
      customerEmail,
      customerPhone,
      address = 'Not specified',
      notes = '',
      bookingId,
      periodType = fromDate === toDate ? 'single-day' : 'multi-day'
    } = bookingDetails;

    // Format dates for display
    const formatDateForDisplay = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formattedFromDate = formatDateForDisplay(fromDate);
    const formattedToDate = formatDateForDisplay(toDate);

    // Determine period display text
    let periodDisplay = '';
    if (fromDate === toDate) {
      periodDisplay = `Date: ${formattedFromDate}`;
    } else {
      periodDisplay = `Period: ${formattedFromDate} to ${formattedToDate}`;
    }

    // Email content
    const subject = `Booking Confirmation - ${serviceType} - ${formattedFromDate}`;

    const body = `Dear Workflow Support,

I would like to confirm the following booking:

BOOKING DETAILS:
────────────────
Service Type: ${serviceType}
${periodDisplay}
Time: ${time}
Duration: ${duration}
Booking Reference: ${bookingId || 'N/A'}
Period Type: ${periodType === 'single-day' ? 'Single Day' : 'Multi-Day'}

CUSTOMER INFORMATION:
────────────────────
Name: ${customerName}
Email: ${customerEmail}
Phone: ${customerPhone}
Address: ${address}

ADDITIONAL NOTES:
────────────────
${notes || 'No additional notes provided.'}

I look forward to the service. Please confirm this booking at your earliest convenience.

Best regards,
${customerName}
${customerEmail}
${customerPhone}

────────────────
This booking was submitted via the Workflow Support booking system on ${new Date().toLocaleDateString('en-GB')}`;

    // Encode for URL
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    // Create mailto link
    const mailtoLink = `mailto:help@nordisksupport.com?subject=${encodedSubject}&body=${encodedBody}`;

    // Open email client
    window.open(mailtoLink, '_blank');

    return true;
  } catch (error) {
    console.error('Error opening email client:', error);
    return false;
  }
};

/**
 * Generates a unique booking ID
 */
export const generateBookingId = (): string => {
  return `NS-${Date.now().toString().slice(-6)}-${Math.random()
    .toString(36)
    .substr(2, 3)
    .toUpperCase()}`;
};

/**
 * Validates date period (ensures toDate is not before fromDate)
 */
export const validateDatePeriod = (fromDate: string, toDate: string): boolean => {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  return to >= from;
};

/**
 * Calculates number of days between two dates
 */
export const calculateDaysBetween = (fromDate: string, toDate: string): number => {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  const diffTime = Math.abs(to.getTime() - from.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
};